import React, { useEffect, useRef } from 'react';

/**
 * Fills the letterbox area around a video with a soft, blurred glow sampled from the video itself
 * (like YouTube's ambient mode) instead of flat black bars. Place it inside the video's positioned
 * container; it finds the sibling <video> automatically. Very cheap: draws a 24px frame a few times a second.
 */
const AmbientBackdrop: React.FC<{ fallback?: string }> = ({ fallback = 'linear-gradient(135deg, #072B2C 0%, #0B6B6F 55%, #17A2A9 100%)' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    let lastTime = -1;
    let drawn = false;

    const draw = () => {
      const video = canvas.parentElement?.querySelector('video');
      if (!video || video.readyState < 2 || document.hidden) return;
      if (drawn && video.currentTime === lastTime) return;
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        lastTime = video.currentTime;
        if (!drawn) {
          drawn = true;
          canvas.style.opacity = '1';
        }
      } catch {
        // Ignore frames that can't be drawn yet.
      }
    };

    draw();
    const timer = window.setInterval(draw, 300);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: fallback, pointerEvents: 'none', zIndex: 0 }}>
      <canvas
        ref={canvasRef}
        width={24}
        height={24}
        style={{
          position: 'absolute',
          inset: '-10%',
          width: '120%',
          height: '120%',
          filter: 'blur(28px) saturate(1.4) brightness(0.85)',
          opacity: 0,
          transition: 'opacity 0.6s ease, background 0.6s ease',
        }}
      />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 100%)' }} />
    </div>
  );
};

export default AmbientBackdrop;
